// ==================== CUSTOMER DASHBOARD SERVICE ====================

import { apiService } from './api';

// ==================== TYPES ====================

export type DateRange = '7d' | '30d' | '90d' | 'this_month' | 'last_month';

export interface DashboardOverviewStats {
  contacts: {
    total: number;
    optedIn: number;
    change: number;
    changeType: 'positive' | 'negative' | 'neutral';
  };
  messages: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    deliveryRate: number;
    readRate: number;
  };
  campaigns: {
    total: number;
    running: number;
    scheduled: number;
    completed: number;
    draft: number;
  };
  templates: {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
  };
  smsTemplates: {
    total: number;
    active: number;
  };
  smsMessages: {
    sent: number;
    delivered: number;
    failed: number;
    deliveryRate: number;
  };
  keywordBots: {
    total: number;
    active: number;
    totalTriggers: number;
  };
  whatsapp: {
    connected: boolean;
    phoneNumber: string | null;
    displayName: string | null;
    qualityRating: string | null;
    messagingLimit: string | null;
    /** Multiple organizations (all organizations shown): every connected number */
    accounts?: Array<{
      organizationId: string;
      businessName: string;
      phoneNumber: string;
      connected: boolean;
    }>;
  };
  sms: {
    connected: boolean;
    provider: string | null;
    senderId: string | null;
    accountName: string | null;
    accountBalance: string | null;
    status: string | null;
  };
  subscription: {
    planName: string;
    status: string;
    contactLimit: number;
    contactsUsed: number;
    messageLimit: number;
    messagesUsed: number;
    campaignLimit: number;
    campaignsUsed: number;
    daysRemaining: number | null;
  };
}

export interface DailyMessageStats {
  date: string;
  sent: number;
  delivered: number;
  read: number;
  failed: number;
}

export interface MessageAnalyticsData {
  dailyStats: DailyMessageStats[];
  totals: {
    sent: number;
    delivered: number;
    read: number;
    failed: number;
    deliveryRate: number;
    readRate: number;
  };
  byType: Array<{
    type: string;
    count: number;
  }>;
}

export interface RecentCampaignItem {
  id: string;
  name: string;
  status: string;
  sentCount: number;
  deliveredCount: number;
  readCount: number;
  failedCount: number;
  recipientCount: number;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface RecentActivityItem {
  id: string;
  type: 'message_sent' | 'message_received' | 'campaign_started' | 'campaign_completed' | 'template_approved' | 'template_rejected' | 'contact_added' | 'bot_triggered';
  title: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface TemplateStatusSummary {
  id: string;
  name: string;
  category: string;
  status: string;
  language: string;
  usageCount: number;
  lastUsedAt: string | null;
}

// ==================== NEW ANALYTICS TYPES ====================

export interface ContactGrowthData {
  dailyData: Array<{
    date: string;
    total: number;
    new: number;
    optedOut: number;
  }>;
  totals: {
    totalContacts: number;
    newContacts: number;
    optedOutContacts: number;
    growthRate: number;
  };
}

export interface KeywordBotAnalytics {
  totalBots: number;
  activeBots: number;
  totalTriggers: number;
  topBots: Array<{
    id: string;
    name: string;
    triggerCount: number;
    isActive: boolean;
  }>;
  triggersByDay: Array<{
    date: string;
    count: number;
  }>;
}

export interface MessageDirectionStats {
  inbound: number;
  outbound: number;
  inboundPercentage: number;
  outboundPercentage: number;
}

export interface TopCampaignItem {
  id: string;
  name: string;
  readRate: number;
  deliveryRate: number;
  sentCount: number;
  readCount: number;
  status: string;
}

export interface EngagementMetrics {
  avgDeliveryRate: number;
  avgReadRate: number;
  avgResponseTime: number | null;
  totalConversations: number;
  activeConversations: number;
  messageDirectionStats: MessageDirectionStats;
}

export interface SmsAnalyticsData {
  totals: {
    sent: number;
    delivered: number;
    failed: number;
    pending: number;
    deliveryRate: number;
  };
  dailyStats: Array<{
    date: string;
    sent: number;
    delivered: number;
    failed: number;
  }>;
  byDirection: {
    inbound: number;
    outbound: number;
  };
  totalSegments: number;
  avgSegmentsPerMessage: number;
}

export interface CustomerDashboardData {
  stats: DashboardOverviewStats;
  messageAnalytics: MessageAnalyticsData;
  recentCampaigns: RecentCampaignItem[];
  recentActivity: RecentActivityItem[];
  templateStatus: TemplateStatusSummary[];
  // New analytics
  contactGrowth: ContactGrowthData;
  keywordBotAnalytics: KeywordBotAnalytics;
  topCampaigns: TopCampaignItem[];
  engagementMetrics: EngagementMetrics;
  smsAnalytics: SmsAnalyticsData;
}

// ==================== SERVICE ====================

class CustomerDashboardService {
  /**
   * Get complete dashboard data
   */
  async getDashboardData(
    dateRange: DateRange = '30d',
    limit: number = 5,
    organizationId?: string,
  ): Promise<CustomerDashboardData> {
    // Multiple organizations: only this organization (default: all)
    const organizationQuery = organizationId ? `&organizationId=${organizationId}` : '';
    const response = await apiService.get<CustomerDashboardData>(
      `/customer/dashboard?dateRange=${dateRange}&limit=${limit}${organizationQuery}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch dashboard data');
    }
    return response.data as CustomerDashboardData;
  }

  /**
   * Get overview stats only
   */
  async getStats(dateRange: DateRange = '30d'): Promise<DashboardOverviewStats> {
    const response = await apiService.get<DashboardOverviewStats>(
      `/customer/dashboard/stats?dateRange=${dateRange}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch stats');
    }
    return response.data as DashboardOverviewStats;
  }

  /**
   * Get message analytics
   */
  async getMessageAnalytics(dateRange: DateRange = '7d'): Promise<MessageAnalyticsData> {
    const response = await apiService.get<MessageAnalyticsData>(
      `/customer/dashboard/message-analytics?dateRange=${dateRange}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch message analytics');
    }
    return response.data as MessageAnalyticsData;
  }

  /**
   * Get recent campaigns
   */
  async getRecentCampaigns(limit: number = 5): Promise<RecentCampaignItem[]> {
    const response = await apiService.get<RecentCampaignItem[]>(
      `/customer/dashboard/recent-campaigns?limit=${limit}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch recent campaigns');
    }
    return response.data as RecentCampaignItem[];
  }

  /**
   * Get recent activity
   */
  async getRecentActivity(limit: number = 10): Promise<RecentActivityItem[]> {
    const response = await apiService.get<RecentActivityItem[]>(
      `/customer/dashboard/recent-activity?limit=${limit}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch recent activity');
    }
    return response.data as RecentActivityItem[];
  }

  /**
   * Get template status
   */
  async getTemplateStatus(limit: number = 5): Promise<TemplateStatusSummary[]> {
    const response = await apiService.get<TemplateStatusSummary[]>(
      `/customer/dashboard/templates?limit=${limit}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch template status');
    }
    return response.data as TemplateStatusSummary[];
  }
}

export const customerDashboardService = new CustomerDashboardService();
